import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Text, TextButton } from '@wix/design-system';
import { useEnterpriseRefContext } from 'providers/EnterpriseContextProvider';
import { EnterpriseLanguageToFormURL } from '../../constants';
import s from './Faq.scss';
import { buildEnerpriseUrlLink } from 'utils';

interface FaqAnswerProps {
  faqCode: string;
  isEnterpriseFaqs: boolean;
  locale: string;
  userGuid?: string;
  clientId?: string;
}

export const FaqAnswer: React.FC<FaqAnswerProps> = ({
  faqCode,
  isEnterpriseFaqs,
  locale,
  userGuid,
  clientId,
}) => {
  const answerKey = isEnterpriseFaqs
    ? `enterprise_plans.faq.answer.${faqCode}`
    : `${faqCode}.answer`;
  const linkKey = isEnterpriseFaqs
    ? `enterprise_plans.faq.link.${faqCode}`
    : `${faqCode}.link`;
  const { t } = useTranslation();
  const { scrollToRef } = useEnterpriseRefContext();

  const handleCTAOnClick = () => {
    const url = EnterpriseLanguageToFormURL.get(locale);

    if (url) {
      const enerpriseUrlLink = buildEnerpriseUrlLink({
        localeUrl: url,
        referrer: 'wix-pp-faq',
        locale,
        userGuid,
        clientId,
      });
      window.open(enerpriseUrlLink, '_blank');
    } else {
      return scrollToRef('faq');
    }
  };

  return (
    <Text
      weight='normal'
      size='medium'
      tagName='p'
      className={s.answer}
      dataHook={`faq-answer-${faqCode}`}
      data-testid={`faq-answer-${faqCode}`}
    >
      {t(linkKey).includes('https') ? (
        <FaqAnswerLink answerKey={answerKey} link={linkKey} />
      ) : (
        <FaqAnswerCallback
          answerKey={answerKey}
          link={linkKey}
          callback={() => handleCTAOnClick()}
        />
      )}
    </Text>
  );
};

const FaqAnswerLink = ({ answerKey, link }) => {
  const { t } = useTranslation();
  return (
    <Trans i18nKey={answerKey} value={link}>
      {answerKey}
      <TextButton as='a' size='medium' target='_blank' href={t(link)}>
        {link}
      </TextButton>
    </Trans>
  );
};

const FaqAnswerCallback = ({ answerKey, link, callback }) => {
  return (
    <Trans i18nKey={answerKey} value={link}>
      {answerKey}
      <TextButton as='span' size='medium' onClick={callback}>
        {link}
      </TextButton>
    </Trans>
  );
};
